import { useState } from 'react';
import { motion } from 'framer-motion';
import { Info, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import type { EMCode, PatientTypeDecisionDto } from '../../types/codingAssistant';

interface Props {
  variants: [EMCode, EMCode];
  ambiguityReason: string | null;
  patientTypeDecision: PatientTypeDecisionDto | null;
  onSelect: (patientType: 'new' | 'established') => void;
  onRegenerate: () => void;
  isBusy: boolean;
}

const MDM_COLOR: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  moderate: 'bg-orange-100 text-orange-800',
  low: 'bg-yellow-100 text-yellow-800',
  straightforward: 'bg-blue-100 text-blue-800',
  minimal: 'bg-gray-100 text-gray-800',
};

export const EMCodeAmbiguousChoice: React.FC<Props> = ({
  variants,
  ambiguityReason,
  patientTypeDecision,
  onSelect,
  onRegenerate,
  isBusy,
}) => {
  const [showWhy, setShowWhy] = useState(false);
  const [newVariant, estVariant] = variants;

  const whyText = describeDecision(patientTypeDecision);

  return (
    <div className="space-y-3">
      <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-900">
              No conclusive evidence — suggesting closest codes.
            </p>
            <p className="text-amber-800 text-xs mt-0.5">
              Select the one that applies to this visit, or pick a different code from the catalog below.
            </p>
            {whyText && (
              <button
                onClick={() => setShowWhy(!showWhy)}
                className="flex items-center gap-1 mt-1.5 text-xs text-amber-900 hover:underline"
              >
                {showWhy ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                What we found
              </button>
            )}
            {showWhy && whyText && (
              <p className="text-xs text-amber-800 mt-1 italic">{whyText}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[newVariant, estVariant].map((variant) => (
          <VariantCard
            key={variant.patientType}
            variant={variant}
            onSelect={() => onSelect(variant.patientType)}
            isBusy={isBusy}
          />
        ))}
      </div>

      <button
        onClick={onRegenerate}
        disabled={isBusy}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
      >
        <Zap className="w-4 h-4" />
        Regenerate
      </button>
    </div>
  );
};

const VariantCard: React.FC<{
  variant: EMCode;
  onSelect: () => void;
  isBusy: boolean;
}> = ({ variant, onSelect, isBusy }) => {
  const [descExpanded, setDescExpanded] = useState(false);
  const mdmKey = variant.mdmLevel ?? '';
  const mdmColor = MDM_COLOR[mdmKey] ?? 'bg-gray-100 text-gray-800';
  const mdmLabel = variant.mdmLevel ? `${variant.mdmLevel.toUpperCase()} MDM` : null;
  const isNew = variant.patientType === 'new';
  const heading = isNew ? 'NEW patient' : 'ESTABLISHED patient';
  const description = variant.description ?? '';
  const shouldShowExpand = description.length > 60;

  // Color semantics:
  // - NEW = emerald (fresh, starting)
  // - ESTABLISHED = indigo (continuing, returning)
  const cardTone = isNew
    ? 'border-emerald-200 bg-emerald-50/40 hover:border-emerald-300'
    : 'border-indigo-200 bg-indigo-50/40 hover:border-indigo-300';
  const headingTone = isNew
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-indigo-100 text-indigo-800';
  const buttonTone = isNew
    ? 'bg-emerald-600 hover:bg-emerald-700'
    : 'bg-indigo-600 hover:bg-indigo-700';

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-lg p-3 flex flex-col gap-2 transition-colors ${cardTone}`}
    >
      <span className={`self-start px-2 py-0.5 rounded text-xs font-semibold tracking-wide uppercase ${headingTone}`}>
        {heading}
      </span>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-mono font-bold text-xl text-gray-900">{variant.code}</span>
        {mdmLabel && (
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${mdmColor}`}>
            {mdmLabel}
          </span>
        )}
      </div>
      <p
        className={descExpanded ? 'text-sm text-gray-700' : 'text-sm text-gray-700 line-clamp-2'}
        title={description}
      >
        {description}
      </p>
      {shouldShowExpand && (
        <button
          onClick={() => setDescExpanded(!descExpanded)}
          className="text-xs font-medium text-purple-700 hover:text-purple-900 self-start inline-flex items-center gap-1"
        >
          {descExpanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              See less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              See full description
            </>
          )}
        </button>
      )}
      <button
        onClick={onSelect}
        disabled={isBusy}
        className={`mt-auto px-3 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${buttonTone}`}
      >
        Select this
      </button>
    </motion.div>
  );
};

function describeDecision(d: PatientTypeDecisionDto | null): string | null {
  if (!d || d.state !== 'ambiguous') return null;

  // Transcript explicitly contradicts DB
  if (d.transcriptHint === 'new' && d.transcriptHintPhrase) {
    return `The transcript indicates a first visit ("${d.transcriptHintPhrase}") but prior visits exist in our records. Confirm which applies.`;
  }

  // Standalone with missing specialty
  if (d.specialtyMatch === null && d.priorAppointmentCount > 0 && d.mostRecentPriorDate) {
    return "A prior visit exists but the provider's specialty isn't set, so we can't apply the same-specialty rule.";
  }

  // No information
  if (d.priorAppointmentCount === 0 && d.transcriptHint === 'unknown') {
    return 'The transcript did not include an explicit first-visit or return-visit cue, and no prior visits are available here. Both codes are valid candidates.';
  }

  return null;
}
